<script lang="ts">
  import type {
    CaptureConfig,
    CaptureFieldName,
    HandoffContact,
    HandoffFieldType,
  } from '../lib/types';
  import { isBuiltinHandoffField } from '../lib/types';

  interface Props {
    config: CaptureConfig['handoff'];
    onsubmit: (contact: HandoffContact) => void;
    oncancel?: () => void;
    submitting?: boolean;
    error?: string | null;
    cancelLabel?: string;
    defaultTitle?: string;
    defaultSubmitLabel?: string;
    /**
     * Pre-fill values coming from JWT claims or `sources`. The user can
     * still edit them before submitting. Missing keys render empty.
     */
    seed?: HandoffContact;
  }

  let {
    config,
    onsubmit,
    oncancel,
    submitting = false,
    error = null,
    cancelLabel = 'Cancelar',
    defaultTitle = 'Antes de pasarte con un humano, compártenos:',
    defaultSubmitLabel = 'Pasar a un humano',
    seed,
  }: Props = $props();

  // Values keyed by `field` (built-in name OR custom key). We seed
  // built-ins from the dedicated columns and custom keys from
  // `seed.custom[key]` so resumed sessions remember what the user typed.
  let values = $state<Record<string, string>>({
    name: seed?.name ?? '',
    email: seed?.email ?? '',
    business: seed?.business ?? '',
    phone: seed?.phone ?? '',
    ...(seed?.custom ?? {}),
  });

  const activeFields = $derived(
    (config.fields ?? []).filter((f) => f.enabled),
  );

  const title = $derived(config.title ?? defaultTitle);
  const submitLabel = $derived(config.submitLabel ?? defaultSubmitLabel);

  type AutocompleteValue = 'email' | 'tel' | 'name' | 'organization' | 'off';
  function autocompleteForBuiltin(name: CaptureFieldName): AutocompleteValue {
    if (name === 'email') return 'email';
    if (name === 'phone') return 'tel';
    if (name === 'name') return 'name';
    if (name === 'business') return 'organization';
    return 'off';
  }

  function inputTypeFor(field: string, type?: HandoffFieldType): string {
    if (isBuiltinHandoffField(field)) {
      if (field === 'email') return 'email';
      if (field === 'phone') return 'tel';
      return 'text';
    }
    if (type === 'email') return 'email';
    if (type === 'phone') return 'tel';
    return 'text';
  }

  function handleSubmit(e: SubmitEvent): void {
    e.preventDefault();
    if (submitting) return;
    const contact: HandoffContact = {};
    const customValues: Record<string, string> = {};
    for (const field of activeFields) {
      const raw = values[field.field]?.trim() ?? '';
      if (raw.length === 0) continue;
      if (isBuiltinHandoffField(field.field)) {
        contact[field.field] = raw;
      } else {
        customValues[field.field] = raw;
      }
    }
    if (Object.keys(customValues).length > 0) {
      contact.custom = customValues;
    }
    onsubmit(contact);
  }

  function update(name: string, value: string): void {
    values = { ...values, [name]: value };
  }
</script>

<form class="hd-handoff" onsubmit={handleSubmit} novalidate={false}>
  <p class="hd-handoff__title">{title}</p>

  {#if error}
    <div class="hd-handoff__error" role="alert">{error}</div>
  {/if}

  <div class="hd-handoff__fields">
    {#each activeFields as field (field.field)}
      {@const builtin = isBuiltinHandoffField(field.field)}
      {@const isTextarea = !builtin && field.type === 'textarea'}
      <label class="hd-handoff__field">
        <span class="hd-handoff__label">
          {field.label}
          {#if field.required}
            <span class="hd-handoff__req" aria-hidden="true">*</span>
          {/if}
        </span>
        {#if isTextarea}
          <textarea
            class="hd-handoff__input hd-handoff__textarea"
            rows="3"
            placeholder={field.placeholder ?? ''}
            required={field.required}
            disabled={submitting}
            value={values[field.field] ?? ''}
            oninput={(e) =>
              update(field.field, (e.currentTarget as HTMLTextAreaElement).value)}
            aria-required={field.required}
          ></textarea>
        {:else}
          <input
            class="hd-handoff__input"
            type={inputTypeFor(field.field, field.type)}
            autocomplete={builtin ? autocompleteForBuiltin(field.field as CaptureFieldName) : 'off'}
            placeholder={field.placeholder ?? ''}
            required={field.required}
            disabled={submitting}
            value={values[field.field] ?? ''}
            oninput={(e) =>
              update(field.field, (e.currentTarget as HTMLInputElement).value)}
            aria-required={field.required}
          />
        {/if}
      </label>
    {/each}
  </div>

  <div class="hd-handoff__actions">
    {#if oncancel}
      <button
        type="button"
        class="hd-handoff__ghost"
        onclick={() => oncancel?.()}
        disabled={submitting}
      >
        {cancelLabel}
      </button>
    {/if}
    <button type="submit" class="hd-handoff__submit" disabled={submitting}>
      {submitting ? '…' : submitLabel}
    </button>
  </div>
</form>

<style>
  .hd-handoff {
    flex: 1 1 auto;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 18px 20px 20px;
    background: var(--hd-surface, #ffffff);
    color: var(--hd-fg, #0f172a);
  }
  .hd-handoff__title {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.45;
    color: var(--hd-fg, #0f172a);
  }
  .hd-handoff__error {
    font-size: 13px;
    color: #b91c1c;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 8px 10px;
    line-height: 1.35;
  }
  .hd-handoff__fields {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .hd-handoff__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .hd-handoff__label {
    font-size: 12px;
    font-weight: 500;
    color: var(--hd-fg-muted, #64748b);
    line-height: 1.3;
  }
  .hd-handoff__req {
    color: #dc2626;
    margin-left: 2px;
  }
  .hd-handoff__input {
    width: 100%;
    border: 1px solid var(--hd-border, #e2e8f0);
    border-radius: 10px;
    padding: 10px 12px;
    font: inherit;
    font-size: 14px;
    line-height: 1.4;
    color: var(--hd-fg, #0f172a);
    background: var(--hd-surface, #ffffff);
    outline: none;
    box-sizing: border-box;
    transition:
      border-color 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      box-shadow 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .hd-handoff__input:focus {
    border-color: var(--hd-primary, #2563eb);
    box-shadow: 0 0 0 3px rgb(var(--hd-primary-rgb, 37 99 235) / 0.15);
  }
  .hd-handoff__input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .hd-handoff__textarea {
    min-height: 70px;
    resize: vertical;
    font-family: inherit;
  }
  .hd-handoff__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
  }
  .hd-handoff__ghost {
    flex: 0 0 auto;
    height: 38px;
    border-radius: 10px;
    border: 1px solid var(--hd-border, #e2e8f0);
    background: transparent;
    color: var(--hd-fg-muted, #64748b);
    padding: 0 14px;
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .hd-handoff__ghost:hover:not([disabled]) {
    background: var(--hd-surface-muted, #f8fafc);
    color: var(--hd-fg, #0f172a);
  }
  .hd-handoff__submit {
    flex: 1 1 auto;
    height: 38px;
    border-radius: 10px;
    border: none;
    background: var(--hd-primary, #2563eb);
    color: var(--hd-primary-fg, #ffffff);
    padding: 0 16px;
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 1px 2px rgb(var(--hd-primary-rgb, 37 99 235) / 0.3);
    transition:
      transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      box-shadow 180ms cubic-bezier(0.2, 0.8, 0.2, 1),
      opacity 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .hd-handoff__submit:hover:not([disabled]) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px -2px rgb(var(--hd-primary-rgb, 37 99 235) / 0.35);
  }
  .hd-handoff__submit:active:not([disabled]) {
    transform: scale(0.98);
  }
  .hd-handoff__submit:disabled,
  .hd-handoff__ghost:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  @media (prefers-reduced-motion: reduce) {
    .hd-handoff__input,
    .hd-handoff__submit,
    .hd-handoff__ghost {
      transition: none;
    }
  }
</style>
